// Brand-tier switching + Fixed Drug Combination (FDC) engine
// ============================================================================
// Three tiers are exposed to the doctor as a segmented control above the
// Prescription table:
//   Original      → whatever brand the doctor / template / push algo set
//   Economical    → cheapest reliable Indian generic
//   Premium       → innovator / patent-holder brand (highest priced)
//   Combine FDC   → merge compatible individual rows into single-pill FDCs
//                   (reduces pill burden, improves compliance, often cheaper)
//
// The tier switch is NON-DESTRUCTIVE — every mutated row carries `_origBrand`
// so switching back to Original perfectly restores what the doctor typed.
// FDC merges are also reversible — the combined row carries `_fdcParts` with
// the original rows it replaced.

// ---------- Brand tiers per molecule ----------
// Each key is the LOWER-CASED generic name. Values: array of Indian brands
// ordered by price (cheapest first). `premium` is the innovator/originator.
// A slash-joined string is what we write into row.brand_name (matches how
// the rest of the app renders brand strings).
const T = (economical, premium) => ({ economical, premium });

export const BRAND_TIERS = {
  // Diabetes — biguanide, SGLT2, DPP-4, SU, TZD, GLP-1, insulin
  "metformin":            T(["Glycomet", "Obimet"],                    ["Glucophage"]),
  "metformin sr":         T(["Glycomet SR", "Obimet SR"],              ["Glucophage XR"]),
  "empagliflozin":        T(["Emburu", "Empamet"],                     ["Jardiance"]),
  "dapagliflozin":        T(["Dapaford", "Dapamet"],                   ["Forxiga"]),
  "linagliptin":          T(["Ondero", "Linagli"],                     ["Trajenta"]),
  "sitagliptin":          T(["Istavel", "Zita"],                       ["Januvia"]),
  "vildagliptin":         T(["Zomelis", "Vysov"],                      ["Galvus"]),
  "gliclazide mr":        T(["Glizid MR", "Reclide MR"],               ["Diamicron MR"]),
  "glimepiride":          T(["Glimy", "Glimestar"],                    ["Amaryl"]),
  "pioglitazone":         T(["Piozone", "Piolim"],                     ["Pioglar"]),
  "semaglutide":          T(["Rybelsus"],                              ["Ozempic"]),
  "dulaglutide":          T(["Trulicity"],                             ["Trulicity"]),
  "liraglutide":          T(["Victoza"],                               ["Victoza"]),
  "tirzepatide":          T(["Mounjaro"],                              ["Mounjaro"]),
  "insulin glargine":     T(["Basalog", "Glaritus"],                   ["Lantus", "Toujeo"]),
  "insulin degludec":     T(["Zoryl-D"],                               ["Tresiba"]),
  // Lipids
  "rosuvastatin":         T(["Rosuvas", "Rozavel"],                    ["Crestor"]),
  "atorvastatin":         T(["Storvas", "Atorlip"],                    ["Lipitor"]),
  "ezetimibe":            T(["Ezedoc", "Ezetib"],                      ["Zetia"]),
  // BP — ARB / ACEi / CCB / thiazide / MRA
  "telmisartan":          T(["Telsartan", "Telma"],                    ["Micardis"]),
  "losartan":             T(["Losacar", "Repace"],                     ["Losar"]),
  "ramipril":             T(["Ramipres", "Zorem"],                     ["Cardace"]),
  "enalapril":            T(["Envas", "Enam"],                         ["Vasotec"]),
  "amlodipine":           T(["Amlodac", "Stamlo"],                     ["Amlong", "Norvasc"]),
  "chlorthalidone":       T(["Hythalton"],                             ["Thalitone"]),
  "indapamide sr":        T(["Lorvas SR"],                             ["Natrilix SR"]),
  "hydrochlorothiazide":  T(["Aquazide"],                              ["Hydrazide"]),
  // HF / arrhythmia / anti-anginal
  "bisoprolol":           T(["Corbis", "Bisoheart"],                   ["Concor"]),
  "carvedilol":           T(["Carvistar", "Cardivas"],                 ["Carca"]),
  "metoprolol succinate": T(["Metpure XL", "Betaloc"],                 ["Seloken XL"]),
  "furosemide":           T(["Frusenex"],                              ["Lasix"]),
  "torsemide":            T(["Torsid", "Diucontin"],                   ["Dytor"]),
  "finerenone":           T(["Kerendia"],                              ["Kerendia"]),
  // Antiplatelet / anticoagulant
  "aspirin":              T(["Loprin", "Ecosprin"],                    ["Ecosprin"]),
  "clopidogrel":          T(["Clopilet", "Deplatt"],                   ["Plavix"]),
  // GI
  "pantoprazole":         T(["Pan", "Pantop"],                         ["Pantocid", "Protonix"]),
  "rabeprazole":          T(["Rabekind"],                              ["Pariet"]),
  // Thyroid
  "levothyroxine":        T(["Eltroxin", "Thyrofit"],                  ["Thyronorm"]),
  // Bone
  "cholecalciferol (d3)": T(["Calcirol"],                              ["Uprise D3", "D-Rise"]),
  "cholecalciferol":      T(["Calcirol"],                              ["Uprise D3", "D-Rise"]),
  "vitamin d3":           T(["Calcirol"],                              ["Uprise D3", "D-Rise"]),
};

// ---------- FDC catalog ----------
// Each entry declares a set of REQUIRED generics. When every generic is
// present in the current prescription table, the doctor is offered a chip
// "Combine → <fdc>". Accepting collapses those rows into one FDC row.
// `pick(rows)` builds the merged row from the individual ones so dose &
// frequency stay predictable.

const norm = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, " ");
const matchesGeneric = (row, generic) => {
  const g = norm(generic);
  const rowText = norm(`${row.generic_name || ""} ${row.composition || ""}`);
  return rowText.includes(g);
};

// Helper to pull the mg number out of the row's strength (e.g. "500 mg" → 500).
const pickMg = (row) => {
  const m = String(row.strength || row.dose || "").match(/([\d.]+)\s*(mg|mcg|iu|g|u\b)/i);
  return m ? { value: parseFloat(m[1]), unit: (m[2] || "mg").toLowerCase() } : null;
};

// Registry — order matters: TRIPLE combos are tried before pairs so a triple
// FDC (e.g. Telmi+Amlo+Chlorthalidone) always wins over the corresponding pair.
export const FDC_CATALOG = [
  // ---------- Triples ----------
  {
    key: "telma-am-h",
    generics: ["telmisartan", "amlodipine", "chlorthalidone"],
    label: "Telma AMH — Telmisartan + Amlodipine + Chlorthalidone (Triple HTN)",
    make: (rows) => {
      const t = rows[0], a = rows[1], c = rows[2];
      const ts = pickMg(t)?.value || 40;
      const as_ = pickMg(a)?.value || 5;
      const cs = pickMg(c)?.value || 12.5;
      return {
        brand_name: "Telma AMH",
        generic_name: "Telmisartan + Amlodipine + Chlorthalidone",
        composition: `Telmisartan ${ts} mg + Amlodipine ${as_} mg + Chlorthalidone ${cs} mg`,
        strength: `${ts}/${as_}/${cs} mg`,
        route: "Oral", frequency: t.frequency || "OD (morning)", timing: t.timing || "", duration: t.duration || "Ongoing — review at follow-up",
        instructions: "Triple-combo HTN. Recheck BP + K+ + creat at 2-4 wk. Report ankle swelling / dry cough.",
      };
    },
  },
  {
    key: "glimestar-mp",
    generics: ["glimepiride", "metformin", "pioglitazone"],
    label: "Zoryl MP / Glimestar MP — Glimepiride + Metformin + Pioglitazone",
    make: (rows) => {
      const g = rows[0], m = rows[1], p = rows[2];
      const gs = pickMg(g)?.value || 1;
      const ms = pickMg(m)?.value || 500;
      const ps = pickMg(p)?.value || 15;
      return {
        brand_name: "Zoryl MP / Glimestar MP",
        generic_name: "Glimepiride + Metformin + Pioglitazone",
        composition: `Glimepiride ${gs} mg + Metformin ${ms} mg + Pioglitazone ${ps} mg`,
        strength: `${gs}/${ms}/${ps} mg`,
        route: "Oral", frequency: m.frequency || "BD (with meals)", timing: m.timing || "", duration: m.duration || "Ongoing — review at follow-up",
        instructions: "Triple oral anti-diabetic. Recheck HbA1c + LFT + weight at 3 months.",
      };
    },
  },

  // ---------- Doubles ----------
  {
    key: "telma-am",
    generics: ["telmisartan", "amlodipine"],
    label: "Telma AM — Telmisartan + Amlodipine",
    make: (rows) => {
      const t = rows[0], a = rows[1];
      const ts = pickMg(t)?.value || 40;
      const as_ = pickMg(a)?.value || 5;
      return {
        brand_name: "Telma AM",
        generic_name: "Telmisartan + Amlodipine",
        composition: `Telmisartan ${ts} mg + Amlodipine ${as_} mg`,
        strength: `${ts}/${as_} mg`,
        route: "Oral", frequency: t.frequency || "OD (morning)", timing: t.timing || "", duration: t.duration || "Ongoing — review at follow-up",
        instructions: "Dual RAAS + CCB for BP. Recheck BP + K+ 2-4 wk. Amlodipine oedema reduced by telmisartan.",
      };
    },
  },
  {
    key: "telma-h",
    generics: ["telmisartan", "hydrochlorothiazide"],
    label: "Telma H — Telmisartan + Hydrochlorothiazide",
    make: (rows) => {
      const t = rows[0];
      const ts = pickMg(t)?.value || 40;
      return {
        brand_name: "Telma H",
        generic_name: "Telmisartan + Hydrochlorothiazide",
        composition: `Telmisartan ${ts} mg + Hydrochlorothiazide 12.5 mg`,
        strength: `${ts}/12.5 mg`,
        route: "Oral", frequency: t.frequency || "OD (morning)", timing: t.timing || "", duration: t.duration || "Ongoing — review at follow-up",
        instructions: "ARB + thiazide. Recheck K+ / Na+ / creat at 2-4 wk. Not for eGFR < 30.",
      };
    },
  },
  {
    key: "cardace-h",
    generics: ["ramipril", "hydrochlorothiazide"],
    label: "Cardace H — Ramipril + Hydrochlorothiazide",
    make: (rows) => {
      const r = rows[0];
      const rs = pickMg(r)?.value || 5;
      return {
        brand_name: "Cardace H",
        generic_name: "Ramipril + Hydrochlorothiazide",
        composition: `Ramipril ${rs} mg + Hydrochlorothiazide 12.5 mg`,
        strength: `${rs}/12.5 mg`,
        route: "Oral", frequency: r.frequency || "OD (morning)", timing: r.timing || "", duration: r.duration || "Ongoing — review at follow-up",
        instructions: "ACEi + thiazide. Report dry cough / dizziness. Recheck K+ / Na+ 2-4 wk.",
      };
    },
  },
  {
    key: "losar-h",
    generics: ["losartan", "hydrochlorothiazide"],
    label: "Losar H — Losartan + Hydrochlorothiazide",
    make: (rows) => {
      const l = rows[0];
      const ls = pickMg(l)?.value || 50;
      return {
        brand_name: "Losar H / Repace H",
        generic_name: "Losartan + Hydrochlorothiazide",
        composition: `Losartan ${ls} mg + Hydrochlorothiazide 12.5 mg`,
        strength: `${ls}/12.5 mg`,
        route: "Oral", frequency: l.frequency || "OD (morning)", timing: l.timing || "", duration: l.duration || "Ongoing — review at follow-up",
        instructions: "ARB + thiazide. Also lowers urate — useful in gout.",
      };
    },
  },
  {
    key: "rosuvas-ez",
    generics: ["rosuvastatin", "ezetimibe"],
    label: "Rosuvas EZ — Rosuvastatin + Ezetimibe",
    make: (rows) => {
      const r = rows[0];
      const rs = pickMg(r)?.value || 20;
      return {
        brand_name: "Rosuvas EZ / Ezerou",
        generic_name: "Rosuvastatin + Ezetimibe",
        composition: `Rosuvastatin ${rs} mg + Ezetimibe 10 mg`,
        strength: `${rs}/10 mg`,
        route: "Oral", frequency: r.frequency || "OD (bedtime)", timing: r.timing || "", duration: r.duration || "Ongoing — review at follow-up",
        instructions: "Statin + intestinal cholesterol absorption blocker. Recheck lipid + LFT 6-8 wk.",
      };
    },
  },
  {
    key: "atorva-ez",
    generics: ["atorvastatin", "ezetimibe"],
    label: "Atorva EZ — Atorvastatin + Ezetimibe",
    make: (rows) => {
      const a = rows[0];
      const as_ = pickMg(a)?.value || 20;
      return {
        brand_name: "Atorva EZ / Ezerol",
        generic_name: "Atorvastatin + Ezetimibe",
        composition: `Atorvastatin ${as_} mg + Ezetimibe 10 mg`,
        strength: `${as_}/10 mg`,
        route: "Oral", frequency: a.frequency || "OD (bedtime)", timing: a.timing || "", duration: a.duration || "Ongoing — review at follow-up",
        instructions: "Statin + ezetimibe combo. Recheck lipids + LFT 6-8 wk. Preferred in CKD (no dose cap).",
      };
    },
  },
  {
    key: "rosave-a",
    generics: ["rosuvastatin", "aspirin"],
    label: "Rosave A — Rosuvastatin + Aspirin (secondary prevention)",
    make: (rows) => {
      const r = rows[0];
      const rs = pickMg(r)?.value || 10;
      return {
        brand_name: "Rosave A / Rosupride A",
        generic_name: "Rosuvastatin + Aspirin",
        composition: `Rosuvastatin ${rs} mg + Aspirin 75 mg`,
        strength: `${rs}/75 mg`,
        route: "Oral", frequency: "OD (after dinner)", timing: "", duration: r.duration || "Ongoing — review at follow-up",
        instructions: "Post-ASCVD combo — statin + antiplatelet. Take after food. Report bleeding / muscle pain.",
      };
    },
  },
  {
    key: "clopilet-a",
    generics: ["clopidogrel", "aspirin"],
    label: "Clopilet A / Deplatt A — Clopidogrel + Aspirin (DAPT)",
    make: (rows) => ({
      brand_name: "Clopilet A / Deplatt A / Ecosprin AV",
      generic_name: "Clopidogrel + Aspirin",
      composition: "Clopidogrel 75 mg + Aspirin 75 mg",
      strength: "75/75 mg",
      route: "Oral", frequency: rows[0].frequency || "OD (after food)", timing: "", duration: rows[0].duration || "12 months post-PCI, then reassess",
      instructions: "Dual antiplatelet therapy — verify duration with cardiologist. Add PPI cover if PUD history.",
    }),
  },
  {
    key: "jardiance-met",
    generics: ["empagliflozin", "metformin"],
    label: "Jardiance Met — Empagliflozin + Metformin",
    make: (rows) => {
      const e = rows.find((r) => matchesGeneric(r, "empagliflozin"));
      const m = rows.find((r) => matchesGeneric(r, "metformin"));
      const es = pickMg(e)?.value || 10;
      const ms = pickMg(m)?.value || 500;
      return {
        brand_name: "Jardiance Met / Emburu M",
        generic_name: "Empagliflozin + Metformin",
        composition: `Empagliflozin ${es} mg + Metformin ${ms} mg`,
        strength: `${es}/${ms} mg`,
        route: "Oral", frequency: "BD (with meals)", timing: "", duration: m.duration || "Ongoing — review at follow-up",
        instructions: "SGLT2i + Metformin combo. Hold during illness/dehydration. Recheck eGFR + K+ 2-4 wk.",
      };
    },
  },
  {
    key: "xigduo",
    generics: ["dapagliflozin", "metformin"],
    label: "Xigduo XR — Dapagliflozin + Metformin",
    make: (rows) => {
      const d = rows.find((r) => matchesGeneric(r, "dapagliflozin"));
      const m = rows.find((r) => matchesGeneric(r, "metformin"));
      const ds = pickMg(d)?.value || 10;
      const ms = pickMg(m)?.value || 500;
      return {
        brand_name: "Xigduo XR / Dapamet",
        generic_name: "Dapagliflozin + Metformin",
        composition: `Dapagliflozin ${ds} mg + Metformin ${ms} mg`,
        strength: `${ds}/${ms} mg`,
        route: "Oral", frequency: "OD-BD (with meals)", timing: "", duration: m.duration || "Ongoing — review at follow-up",
        instructions: "SGLT2i + Metformin combo. Hold during illness/dehydration.",
      };
    },
  },
  {
    key: "janumet",
    generics: ["sitagliptin", "metformin"],
    label: "Janumet — Sitagliptin + Metformin",
    make: (rows) => {
      const s = rows.find((r) => matchesGeneric(r, "sitagliptin"));
      const m = rows.find((r) => matchesGeneric(r, "metformin"));
      const ss = pickMg(s)?.value || 50;
      const ms = pickMg(m)?.value || 500;
      return {
        brand_name: "Janumet / Istamet",
        generic_name: "Sitagliptin + Metformin",
        composition: `Sitagliptin ${ss} mg + Metformin ${ms} mg`,
        strength: `${ss}/${ms} mg`,
        route: "Oral", frequency: "BD (with meals)", timing: "", duration: m.duration || "Ongoing — review at follow-up",
        instructions: "DPP-4i + Metformin combo. Adjust sitagliptin per eGFR (100/50/25 mg).",
      };
    },
  },
  {
    key: "trajenta-duo",
    generics: ["linagliptin", "metformin"],
    label: "Trajenta Duo — Linagliptin + Metformin",
    make: (rows) => {
      const m = rows.find((r) => matchesGeneric(r, "metformin"));
      const ms = pickMg(m)?.value || 500;
      return {
        brand_name: "Trajenta Duo / Ondero Met",
        generic_name: "Linagliptin + Metformin",
        composition: `Linagliptin 2.5 mg + Metformin ${ms} mg`,
        strength: `2.5/${ms} mg`,
        route: "Oral", frequency: "BD (with meals)", timing: "", duration: m.duration || "Ongoing — review at follow-up",
        instructions: "DPP-4i + Metformin combo. No linagliptin renal adjustment; metformin per eGFR.",
      };
    },
  },
  {
    key: "glimestar-m",
    generics: ["glimepiride", "metformin"],
    label: "Glimestar M / Amaryl M — Glimepiride + Metformin",
    make: (rows) => {
      const g = rows.find((r) => matchesGeneric(r, "glimepiride"));
      const m = rows.find((r) => matchesGeneric(r, "metformin"));
      const gs = pickMg(g)?.value || 1;
      const ms = pickMg(m)?.value || 500;
      return {
        brand_name: `Glimestar M${gs} / Amaryl M`,
        generic_name: "Glimepiride + Metformin",
        composition: `Glimepiride ${gs} mg + Metformin ${ms} mg`,
        strength: `${gs}/${ms} mg`,
        route: "Oral", frequency: "OD-BD (with breakfast / dinner)", timing: "", duration: m.duration || "Ongoing — review at follow-up",
        instructions: "SU + Metformin combo. Skip dose if meal missed. Educate on hypoglycaemia signs.",
      };
    },
  },
  {
    key: "concor-am",
    generics: ["bisoprolol", "amlodipine"],
    label: "Concor AM — Bisoprolol + Amlodipine",
    make: (rows) => {
      const b = rows.find((r) => matchesGeneric(r, "bisoprolol"));
      const a = rows.find((r) => matchesGeneric(r, "amlodipine"));
      const bs = pickMg(b)?.value || 5;
      const as_ = pickMg(a)?.value || 5;
      return {
        brand_name: "Concor AM / Corbis AM",
        generic_name: "Bisoprolol + Amlodipine",
        composition: `Bisoprolol ${bs} mg + Amlodipine ${as_} mg`,
        strength: `${bs}/${as_} mg`,
        route: "Oral", frequency: b.frequency || "OD (morning)", timing: "", duration: b.duration || "Ongoing — review at follow-up",
        instructions: "β-blocker + CCB. For HTN + angina / stable CAD. Report bradycardia / oedema.",
      };
    },
  },
];

// ---------- Public API ----------

// Detect all applicable FDCs for the current rows. Skips insulin & already-
// combined rows. Returns [{key, label, rowIdxs, preview}] in priority order.
// A row can appear in at most one recommendation (first match wins), so triples
// beat pairs.
export function detectFDCs(rows) {
  const consumed = new Set();
  const result = [];
  const usable = rows.map((r, i) => ({ r, i })).filter(({ r }) => r?.brand_name && r.type !== "insulin" && !r._fdcCombined);

  for (const fdc of FDC_CATALOG) {
    const matches = fdc.generics.map((g) => {
      const found = usable.find(({ r, i }) => !consumed.has(i) && matchesGeneric(r, g));
      return found ? found.i : -1;
    });
    if (matches.some((i) => i < 0)) continue;
    matches.forEach((i) => consumed.add(i));
    const rowSlice = matches.map((i) => rows[i]);
    const preview = fdc.make(rowSlice);
    result.push({ key: fdc.key, label: fdc.label, rowIdxs: matches, preview });
  }
  return result;
}

// Apply a single FDC recommendation. Returns a new medicines[] array with the
// matched rows removed and a single combined row inserted at the position of
// the first matched row. The combined row remembers its _fdcParts so
// restoreOriginal() can undo the merge.
export function applyFDC(rows, fdcMatch) {
  const parts = fdcMatch.rowIdxs.map((i) => rows[i]);
  const combined = {
    ...fdcMatch.preview,
    // preserve any per-row instructions verbatim
    _fdcCombined: true,
    _fdcKey: fdcMatch.key,
    _fdcParts: parts.map((p) => ({ ...p })),
  };
  const insertAt = Math.min(...fdcMatch.rowIdxs);
  const drop = new Set(fdcMatch.rowIdxs);
  const kept = rows.filter((_, i) => !drop.has(i));
  return [...kept.slice(0, insertAt), combined, ...kept.slice(insertAt)];
}

export function applyAllFDCs(rows) {
  let next = rows;
  // Re-detect after every apply because indices shift.
  // Cap at 10 rounds as a safety net.
  for (let n = 0; n < 10; n++) {
    const fdcs = detectFDCs(next);
    if (!fdcs.length) break;
    next = applyFDC(next, fdcs[0]);
  }
  return next;
}

// Switch every eligible row's brand_name to the "My Choice" tier — the brand
// belonging to the first company in the doctor's preferred list that carries
// a brand for that molecule. Falls back to the Economical tier when no
// preferred-company brand exists.  See brandCompanies.js for the data.
export function switchToMyChoice(rows, preferredCompanies, pickMyChoiceBrand) {
  return rows.map((row) => {
    if (!row?.brand_name) return row;
    if (row.type === "insulin") return row;
    if (row._fdcCombined) return row;
    const hit = pickMyChoiceBrand(row, preferredCompanies || []);
    if (hit) {
      return {
        ...row,
        _origBrand: row._origBrand ?? row.brand_name,
        _myChoiceCompany: hit.company,
        brand_name: hit.brand,
      };
    }
    // Fallback → Economical
    const key = pickKey(row);
    const tiers = key ? BRAND_TIERS[key] : null;
    const list = tiers?.economical;
    if (!list?.length) return row;
    return {
      ...row,
      _origBrand: row._origBrand ?? row.brand_name,
      _myChoiceCompany: "Economical fallback",
      brand_name: list.join(" / "),
    };
  });
}

// Switch every eligible row's brand_name to the requested tier ("economical"
// or "premium"). Idempotent — reruns replace the brand but only cache the
// original once. Rows without a tier match are left untouched.
export function switchBrandTier(rows, tier /* "economical" | "premium" */) {
  return rows.map((row) => {
    if (!row?.brand_name) return row;
    if (row.type === "insulin") return row; // insulin uses its own brand list
    // Leave combined FDC rows alone — a tier swap would remap the FDC brand
    // to a single-molecule brand and the combination visually "disappears".
    // Doctor should Restore Original first, then swap tier.
    if (row._fdcCombined) return row;
    const key = pickKey(row);
    const tiers = key ? BRAND_TIERS[key] : null;
    if (!tiers) return row;
    const list = tiers[tier];
    if (!list?.length) return row;
    return {
      ...row,
      _origBrand: row._origBrand ?? row.brand_name,
      brand_name: list.join(" / "),
    };
  });
}

// Restore each row to Original tier — undoes brand swap and FDC merges.
export function restoreOriginal(rows) {
  const out = [];
  for (const row of rows) {
    if (row._fdcCombined && Array.isArray(row._fdcParts)) {
      // Unpack combined row back into parts, stripping our internal fields.
      for (const p of row._fdcParts) {
        const { _origBrand, _fdcCombined, _fdcKey, _fdcParts, _myChoiceCompany, ...clean } = p;
        out.push({
          ...clean,
          brand_name: _origBrand || clean.brand_name,
        });
      }
    } else if (row._origBrand) {
      const { _origBrand, _myChoiceCompany, ...rest } = row;
      out.push({ ...rest, brand_name: _origBrand });
    } else {
      out.push(row);
    }
  }
  return out;
}

// ---------- helpers ----------

// Pull a normalised molecule key out of the row using generic / composition /
// brand — whichever matches an entry in BRAND_TIERS first wins.
function pickKey(row) {
  const cand = [row.generic_name, row.composition, row.brand_name]
    .filter(Boolean)
    .map((s) => norm(s));
  for (const c of cand) {
    if (BRAND_TIERS[c]) return c;
    // longest-key first substring match — handles "Metformin (immediate release)" → "metformin"
    const keys = Object.keys(BRAND_TIERS).sort((a, b) => b.length - a.length);
    for (const k of keys) {
      if (c.includes(k)) return k;
    }
  }
  return null;
}
